import { useEffect, useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import SlotImage from '@/components/base/SlotImage';
import { useCollection } from '@/hooks/useCollection';
import type { GalleryImage } from '@/lib/types';

// Preferred browsing order for the category filter tabs.
const CATEGORY_ORDER = ['Worship', 'Graduation', 'Outreach', 'Projects'];

export default function HighlightsPage() {
  const { rows: images, loading, error, reload } = useCollection<GalleryImage>('gallery_images', 'sort_order', true);
  const published = images.filter((img) => img.is_published !== false);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [visible, setVisible] = useState(12);
  const [activeCat, setActiveCat] = useState('All');

  const presentCats = Array.from(
    new Set(published.map((img) => (img.category || '').trim()).filter(Boolean)),
  );
  const orderedCats = [
    ...CATEGORY_ORDER.filter((c) => presentCats.includes(c)),
    ...presentCats.filter((c) => !CATEGORY_ORDER.includes(c)).sort(),
  ];
  const categories = ['All', ...orderedCats];

  const list = activeCat === 'All' ? published : published.filter((img) => (img.category || '').trim() === activeCat);

  useEffect(() => {
    document.title = 'Highlights Gallery — TGMCI Ministry Photos';
    window.scrollTo(0, 0);
  }, []);

  // Reset the load-more counter whenever the visitor switches category.
  useEffect(() => {
    setVisible(12);
    setLightboxIdx(null);
  }, [activeCat]);

  // Keyboard navigation for lightbox
  useEffect(() => {
    if (lightboxIdx === null) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxIdx(null);
      if (e.key === 'ArrowRight') setLightboxIdx((prev) => prev !== null ? (prev + 1) % list.length : null);
      if (e.key === 'ArrowLeft') setLightboxIdx((prev) => prev !== null ? (prev - 1 + list.length) % list.length : null);
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIdx, list.length]);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-16 bg-background-950 text-center overflow-hidden">
          <div className="absolute inset-0">
            <SlotImage
              slot="highlights_hero"
              fallback="https://storage.helloreaddy.io/project_files/e45ec48e-345c-4840-a547-bcc42428da39/47fcfdce-9fca-4de5-be2f-48ee60c3afb0_compressed_WhatsApp-Image-2026-09-12-at-15.29.03-8.webp"
              alt="Gallery background"
              className="w-full h-full object-cover object-top opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-950/90 to-background-950" />
          </div>
          <div className="relative z-10 max-w-[800px] mx-auto px-4">
            <p className="text-accent-400 font-label text-sm tracking-widest uppercase font-semibold mb-3">Our Gallery</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-white leading-tight mb-4">
              Our Ministry Highlights
            </h1>
            <div className="w-12 h-1 bg-accent-500 mx-auto mb-6" />
            <p className="text-white/60 text-base leading-relaxed">
              A visual journey through our church events, outreach programs, worship services, and community life.
            </p>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8">
            {loading ? (
              <p className="text-center text-foreground-400 py-16">
                <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading gallery...
              </p>
            ) : error ? (
              <div className="text-center py-16">
                <p className="text-red-600 text-sm mb-3">Could not load the gallery.</p>
                <button type="button" onClick={reload} className="btn-primary">Retry</button>
              </div>
            ) : published.length === 0 ? (
              <p className="text-center text-foreground-400 py-16">No photos yet.</p>
            ) : (
              <>
                {/* Category filter */}
                <div className="flex flex-wrap items-center justify-center gap-2 mb-10" role="tablist" aria-label="Filter gallery by category">
                  {categories.map((cat) => {
                    const count = cat === 'All' ? published.length : published.filter((img) => (img.category || '').trim() === cat).length;
                    const isActive = activeCat === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        onClick={() => setActiveCat(cat)}
                        className={`px-4 py-2 rounded-full text-sm font-label font-medium border transition-colors cursor-pointer whitespace-nowrap ${
                          isActive
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-background-50 text-foreground-600 border-background-300 hover:border-primary-400 hover:text-primary-600'
                        }`}
                      >
                        {cat}
                        <span className={`ml-1.5 text-xs ${isActive ? 'text-white/70' : 'text-foreground-400'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>

                {list.length === 0 ? (
                  <p className="text-center text-foreground-400 py-16">No photos in this category yet.</p>
                ) : (
                  <>
                    <div className="columns-2 sm:columns-3 lg:columns-4 gap-4 space-y-0">
                      {list.slice(0, visible).map((img, idx) => (
                        <div key={img.id} className="break-inside-avoid mb-4">
                          <button
                            type="button"
                            onClick={() => setLightboxIdx(idx)}
                            className="block w-full img-zoom rounded-xl overflow-hidden cursor-zoom-in group relative"
                            aria-label={`Open image: ${img.alt}`}
                          >
                            <img
                              src={img.src}
                              alt={img.alt}
                              className="w-full h-auto object-cover object-top"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
                              <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full">
                                <i className="ri-zoom-in-line text-foreground-800" aria-hidden="true" />
                              </div>
                            </div>
                            {img.category && (
                              <span className="absolute top-3 left-3 text-[11px] font-label font-semibold bg-white/90 text-foreground-700 px-2.5 py-1 rounded-full">
                                {img.category}
                              </span>
                            )}
                          </button>
                        </div>
                      ))}
                    </div>

                    {visible < list.length && (
                      <div className="text-center mt-10">
                        <button
                          type="button"
                          onClick={() => setVisible((v) => v + 12)}
                          className="btn-primary"
                        >
                          Load More
                          <i className="ri-add-line" aria-hidden="true" />
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-primary-800" />
          <div className="relative z-10 max-w-[900px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-2">
                We Appreciate Your Time With Us
              </h2>
              <p className="text-white/70 text-base leading-relaxed max-w-lg">
                We are passionate in soul winning, rescuing men and women from the kingdom of darkness and bringing them into the kingdom of God. — Colossians 1:13
              </p>
            </div>
            <a href="/contact" className="btn-accent shrink-0 whitespace-nowrap">
              Get in Touch
            </a>
          </div>
        </section>
      </main>

      {/* Lightbox */}
      {lightboxIdx !== null && list[lightboxIdx] && (
        <div
          className="lightbox-overlay"
          onClick={() => setLightboxIdx(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          <button
            type="button"
            className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
            onClick={() => setLightboxIdx(null)}
            aria-label="Close image viewer"
          >
            <i className="ri-close-line text-xl" aria-hidden="true" />
          </button>

          <button
            type="button"
            className="absolute left-4 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((prev) => prev !== null ? (prev - 1 + list.length) % list.length : null); }}
            aria-label="Previous image"
          >
            <i className="ri-arrow-left-line text-xl" aria-hidden="true" />
          </button>

          <div
            className="max-w-[90vw] max-h-[85vh] mx-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={list[lightboxIdx].src}
              alt={list[lightboxIdx].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white/70 text-sm text-center mt-3">
              {list[lightboxIdx].title || list[lightboxIdx].alt} — {lightboxIdx + 1} / {list.length}
            </p>
            {list[lightboxIdx].description && (
              <p className="text-white/50 text-xs text-center mt-1 max-w-xl mx-auto">
                {list[lightboxIdx].description}
              </p>
            )}
          </div>

          <button
            type="button"
            className="absolute right-4 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white cursor-pointer transition-colors"
            onClick={(e) => { e.stopPropagation(); setLightboxIdx((prev) => prev !== null ? (prev + 1) % list.length : null); }}
            aria-label="Next image"
          >
            <i className="ri-arrow-right-line text-xl" aria-hidden="true" />
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
}